import api from '../../services/api';

const MULTIPART_CONFIG = { headers: { 'Content-Type': 'multipart/form-data' } };

/**
 * @typedef {object} MassiveZonePayload
 * @property {string} name
 * @property {string} courier
 * @property {number|string} price
 * @property {number|string} admin - Admin id, sent as `admin_id`
 * @property {string} [tms]
 * @property {File} [file] - Excel sheet holding the zone coverage
 */

/**
 * Builds the multipart body for zones whose coverage is managed through an
 * Excel sheet (Mexico). Note the backend expects `admin_id` here, while the
 * JSON endpoints expect `admin`.
 *
 * @param {MassiveZonePayload} zoneData
 * @returns {FormData}
 */
const buildZoneFormData = (zoneData) => {
  const formData = new FormData();
  formData.append('name', zoneData.name);
  formData.append('courier', zoneData.courier);
  formData.append('price', zoneData.price);
  formData.append('admin_id', zoneData.admin);

  if (zoneData.tms) {
    formData.append('tms', zoneData.tms);
  }

  if (zoneData.file) {
    formData.append('file', zoneData.file);
  }

  return formData;
};

/* -------------------------------------------------------------------------- */
/*                                    Zones                                   */
/* -------------------------------------------------------------------------- */

/**
 * Fetches a paginated page of shipping zones.
 *
 * @param {{page: number, page_size: number, search?: string}} params
 */
export const getZonesRequest = async (params) => {
  return await api.get('shipping_zones/list/', { params });
};

/**
 * Fetches a single zone along with its `communes_set`.
 *
 * @param {number|string} zoneId
 */
export const getZoneDetailRequest = async (zoneId) => {
  return await api.get(`shipping_zones/${zoneId}/detail/`);
};

/**
 * Creates a zone whose communes are picked one by one (Chile).
 *
 * @param {{name: string, courier: string, admin: number, price: number, country: string, commune_ids: Array<number>}} payload
 */
export const createZoneRequest = async (payload) => {
  return await api.post('shipping_zones/create/', payload);
};

/**
 * Creates a zone whose coverage is uploaded as an Excel sheet (Mexico).
 *
 * @param {MassiveZonePayload} zoneData
 * @param {(event: ProgressEvent) => void} [onUploadProgress]
 */
export const createZoneMassiveRequest = async (zoneData, onUploadProgress) => {
  return await api.post(
    'shipping_zones/create_massive/',
    buildZoneFormData(zoneData),
    { ...MULTIPART_CONFIG, onUploadProgress }
  );
};

/**
 * @param {number|string} zoneId
 * @param {object} payload - Partial zone fields to patch
 */
export const updateZoneRequest = async (zoneId, payload) => {
  return await api.patch(`shipping_zones/${zoneId}/update/`, payload);
};

/**
 * Replaces the coverage of a massive zone with a new Excel sheet.
 *
 * @param {number|string} zoneId
 * @param {MassiveZonePayload} zoneData
 * @param {(event: ProgressEvent) => void} [onUploadProgress]
 */
export const updateZoneMassiveRequest = async (zoneId, zoneData, onUploadProgress) => {
  return await api.patch(
    `shipping_zones/${zoneId}/update_massive/`,
    buildZoneFormData(zoneData),
    { ...MULTIPART_CONFIG, onUploadProgress }
  );
};

/** @param {number|string} zoneId */
export const deleteZoneRequest = async (zoneId) => {
  return await api.delete(`shipping_zones/${zoneId}/delete/`);
};

/* -------------------------------------------------------------------------- */
/*                            Coverage (communes)                             */
/* -------------------------------------------------------------------------- */

/**
 * Fetches the communes covered by a zone. Server-side paginated: a single zone
 * may hold hundreds of thousands of rows.
 *
 * @param {number|string} zoneId
 * @param {{page: number, page_size: number, search?: string}} params
 */
export const getZoneCommunesRequest = async (zoneId, params) => {
  return await api.get(`shipping_zones/${zoneId}/communes/`, { params });
};

/* -------------------------------------------------------------------------- */
/*                        Warehouse ↔ zone assignments                        */
/* -------------------------------------------------------------------------- */

/**
 * Returns a plain (non paginated) array of assignments for the zone.
 *
 * @param {number|string} zoneId
 */
export const getZoneAssignmentsRequest = async (zoneId) => {
  return await api.get(`warehouses/list_assignments_for_zone/${zoneId}/`);
};

/**
 * @param {{warehouse: number, shipping_method: string, shipping_zone: number}} payload
 */
export const createAssignmentRequest = async (payload) => {
  return await api.post('warehouses/create_warehouse_shipping_zone/', payload);
};

/**
 * @param {number|string} assignmentId
 * @param {{shipping_method: string}} payload
 */
export const updateAssignmentRequest = async (assignmentId, payload) => {
  return await api.patch(
    `warehouses/update_warehouse_shipping_zone/${assignmentId}/`,
    payload
  );
};

/** @param {number|string} assignmentId */
export const deleteAssignmentRequest = async (assignmentId) => {
  return await api.delete(`warehouses/delete_warehouse_shipping_zone/${assignmentId}/`);
};

/* -------------------------------------------------------------------------- */
/*                              Form select options                           */
/* -------------------------------------------------------------------------- */

/** Couriers already come shaped as `{ value, label }` from the backend. */
export const getCourierOptionsRequest = async () => {
  return await api.get('shipping_zones/options/couriers/');
};

export const getAdminOptionsRequest = async () => {
  return await api.get('admins/list/');
};

export const getWarehouseOptionsRequest = async () => {
  return await api.get('warehouses/');
};

/**
 * Zones belonging to one admin, for the forms that attach something to a zone.
 *
 * Distinct from `getZonesRequest`, which is the paginated table: this one is
 * the whole set, scoped by owner, and is what a warehouse has to choose from.
 *
 * @param {number|string} adminId
 */
export const getAdminShippingZonesRequest = async (adminId) => {
  return await api.get(`shipping_zones/get_admin_shipping_zones/${adminId}/`);
};
